import React from 'react';
import Button from './Button';

const WorkflowNavigation = ({
  currentStep = 1,
  totalSteps = 3,
  onNext,
  onPrevious,
  onCancel,
  isNextDisabled = false,
  isPreviousDisabled = false,
  isLoading = false,
  nextLabel = 'Continuer',
  previousLabel = 'Retour',
  cancelLabel = 'Annuler',
  showCancel = false,
  className = ''
}) => {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;

  const handleNext = () => {
    if (onNext && !isNextDisabled && !isLoading) {
      onNext();
    }
  };

  const handlePrevious = () => {
    if (onPrevious && !isPreviousDisabled && !isLoading) {
      onPrevious();
    }
  };

  const handleCancel = () => {
    if (onCancel && !isLoading) {
      onCancel();
    }
  };

  const getFinalLabel = () => {
    if (isLastStep) return 'Terminer';
    return nextLabel;
  };

  return (
    <div className="workflow-navigation">
      <div className="workflow-navigation-container">
        <div className="workflow-navigation-left">
          {showCancel && (
            <Button
              variant="ghost"
              onClick={handleCancel}
              disabled={isLoading}
              iconName="X"
              iconPosition="left"
              className="text-gray-400 hover:text-white hover:bg-white/5 border border-white/10 hover:border-white/20 transition-all duration-300"
            >
              {cancelLabel}
            </Button>
          )}
        </div>

        <div className="workflow-navigation-right">
          {!isFirstStep && (
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={isPreviousDisabled || isLoading}
              iconName="ChevronLeft"
              iconPosition="left"
              className="border-white/20 text-gray-300 hover:text-white hover:bg-white/5 hover:border-white/30 transition-all duration-300"
            >
              {previousLabel}
            </Button>
          )}

          <Button
            variant="default"
            onClick={handleNext}
            disabled={isNextDisabled || isLoading}
            loading={isLoading}
            iconName={isLastStep ? 'Check' : 'ArrowRight'}
            iconPosition="right"
            className="premium-cta-button"
          >
            {getFinalLabel()}
          </Button>
        </div>
      </div>

      <style jsx>{`
        .workflow-navigation {
          position: relative;
          background: rgba(15, 15, 18, 0.95);
          backdrop-filter: blur(40px) saturate(180%);
          -webkit-backdrop-filter: blur(40px) saturate(180%);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding: 1.5rem 0;
          box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.4);
        }

        .workflow-navigation-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .workflow-navigation-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .workflow-navigation-right {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-left: auto;
        }

        /* Premium CTA Button Styling */
        :global(.premium-cta-button) {
          background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%) !important;
          color: #FFFFFF !important;
          font-weight: 600 !important;
          padding: 0.875rem 2rem !important;
          border-radius: 12px !important;
          border: 1px solid rgba(96, 165, 250, 0.3) !important;
          box-shadow: 
            0 4px 16px rgba(59, 130, 246, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.1) inset !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          font-size: 1rem !important;
          letter-spacing: 0.01em !important;
        }

        :global(.premium-cta-button:hover:not(:disabled)) {
          background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%) !important;
          box-shadow: 
            0 6px 24px rgba(59, 130, 246, 0.4),
            0 0 0 1px rgba(255, 255, 255, 0.15) inset !important;
          transform: translateY(-1px) !important;
          border-color: rgba(96, 165, 250, 0.5) !important;
        }

        :global(.premium-cta-button:active:not(:disabled)) {
          transform: translateY(0) !important;
          box-shadow: 
            0 2px 12px rgba(59, 130, 246, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.1) inset !important;
        }

        :global(.premium-cta-button:disabled) {
          background: rgba(59, 130, 246, 0.3) !important;
          border-color: rgba(96, 165, 250, 0.2) !important;
          box-shadow: none !important;
          cursor: not-allowed !important;
          opacity: 0.5 !important;
        }

        @media (max-width: 640px) {
          .workflow-navigation {
            padding: 1rem 0;
          }

          .workflow-navigation-container {
            padding: 0 1rem;
            flex-direction: column;
            align-items: stretch;
          }

          .workflow-navigation-left,
          .workflow-navigation-right {
            width: 100%;
            justify-content: center;
          }

          .workflow-navigation-right {
            margin-left: 0;
            flex-direction: column-reverse;
            gap: 0.75rem;
          }

          .workflow-navigation-right > * {
            width: 100%;
          }

          :global(.premium-cta-button) {
            padding: 1rem 1.5rem !important;
            font-size: 1.05rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WorkflowNavigation;